<template>
  <div class="flex items-center">
    <div class="relative">
      <button
        @click="toggleNotifications"
        class="flex mx-4 text-gray-600 focus:outline-none mr-6 relative"
      >
        <v-icon :icon="mdiBellOutline" size="24" color="primary" />

        <span
          v-if="unreadCount > 0"
          class="absolute -top-1 -right-1 bg-negative text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 outline outline-2 outline-white"
        >
          {{ unreadCount > 99 ? '99+' : unreadCount }}
        </span>
      </button>

      <div
        v-show="notificationOpen"
        @click="notificationOpen = false"
        class="fixed inset-0 h-full w-full z-30"
      ></div>

      <transition
        enter-active-class="transition duration-150 ease-out transform"
        enter-from-class="scale-95 opacity-0"
        enter-to-class="scale-100 opacity-100"
        leave-active-class="transition duration-150 ease-in transform"
        leave-from-class="scale-100 opacity-100"
        leave-to-class="scale-95 opacity-0"
      >
        <div
          v-show="notificationOpen"
          class="absolute right-0 mt-2 w-[24rem] bg-white rounded-xl shadow-md overflow-hidden z-30 border border-gray-200/80"
        >
          <div class="flex justify-between p-4 items-center">
            <div class="flex items-center gap-2">
              <span
                v-if="unreadCount > 0"
                class="bg-negative text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
              >
                {{ unreadCount > 99 ? '99+' : unreadCount }}
              </span>
              <h3 class="text-lg font-bold text-textPrimary">Notificações</h3>
            </div>

            <div class="flex items-center gap-4">
              <button
                v-if="unreadCount > 0"
                @click="handleMarkAllAsRead"
                class="text-xs text-primary hover:text-primary20 font-medium"
              >
                Marcar todas como lidas
              </button>

              <button
                @click="notificationOpen = false"
                class="text-gray-400 hover:text-gray-600 transition-colors duration-150"
              >
                <v-icon :icon="mdiClose" size="20" />
              </button>
            </div>
          </div>

          <div v-if="isLoading && notifications.length === 0" class="max-h-[28rem] overflow-y-auto">
            <NotificationItemSkeleton v-for="n in 4" :key="`skeleton-${n}`" />
          </div>

          <div v-else-if="notifications.length > 0" class="max-h-[28rem] overflow-y-auto">
            <div
              v-for="notification in notifications"
              :key="notification.id"
              class="relative flex items-start gap-3 p-3 border-b border-gray-100 hover:bg-gray-50"
              :style="!notification.isRead ? { backgroundColor: '#D8EEF3A3' } : {}"
            >
              <div
                class="flex-shrink-0 flex items-center justify-center p-2 gap-2 rounded-full bg-primary30 mt-1"
              >
                <DoctorNotificationIcon />
              </div>

              <div class="flex-1 min-w-0 text-left">
                <p
                  class="text-base font-normal text-secondary50 leading-6 tracking-[0.08px] line-clamp-1 cursor-pointer"
                  @click="handleNotificationClick(notification)"
                >
                  {{ notification.title }}
                </p>
                <p
                  class="text-sm font-normal text-[#68688D] leading-[18px] tracking-[0.07px] mt-0.5 overflow-hidden cursor-pointer"
                  style="display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical"
                  @click.stop="openDetailModal(notification)"
                >
                  {{ notification.message }}
                </p>
                <button
                  v-if="isMessageLong(notification.message)"
                  @click.stop="openDetailModal(notification)"
                  class="text-xs text-primary hover:text-primary20 font-medium mt-0.5"
                >
                  Leia mais
                </button>
                <div class="flex items-center gap-2 mt-1">
                  <p class="text-xs font-normal text-[#68688D] leading-[16px] tracking-[0.07px]">
                    {{ formatTime(notification.createdAt) }}
                  </p>
                </div>
              </div>

              <span
                v-if="!notification.isRead"
                class="absolute top-3 right-3 w-2.5 h-2.5 rounded-full bg-negative flex-shrink-0"
              ></span>
            </div>

            <button
              v-if="hasMore && !isLoading"
              @click="handleLoadMore"
              class="w-full p-3 text-sm text-primary hover:bg-gray-50 font-medium"
            >
              Carregar mais
            </button>
            <div v-if="isLoading" class="w-full p-3 text-center text-sm text-gray-400">
              Carregando...
            </div>
          </div>

          <div v-else class="p-4 text-center text-sm text-gray-500">
            Você não tem nenhuma notificação no momento.
          </div>
        </div>
      </transition>
    </div>
  </div>

  <!-- Modal "Leia mais" usando Modal base do projeto -->
  <Modal
    :modalActive="modals[EModals.NOTIFICATION_DETAIL]"
    :hasHeaderBorder="true"
    maxWidth="max-w-lg"
    maxHeight="60vh"
    @close="closeDetailModal"
  >
    <template #header>
      <h3 class="text-lg font-bold text-textPrimary text-left">
        {{ detailNotification?.title }}
      </h3>
    </template>

    <p class="text-sm text-[#68688D] leading-6 whitespace-pre-line text-left">
      {{ detailNotification?.message }}
    </p>

    <template #footer>
      <div class="flex justify-end gap-4">
        <OutlineButton text="Fechar" width="120px" height="42px" @click="closeDetailModal" />
        <FilledButton
          v-if="detailNotification?.targetUrl"
          text="Ver detalhes"
          width="140px"
          height="42px"
          color="primary"
          @click="handleDetailAction"
        />
      </div>
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { mdiBellOutline, mdiClose } from '@mdi/js'
import { useRouter } from 'vue-router'
import { useNotificationStore } from '@/pinia-store/useNotificationStore'
import { useModalStore, EModals } from '@/pinia-store/useModalStore'
import { useErrorToast } from '@/composables/useErrorToast'
import Modal from '@/components/designSystem/modals/Modal.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import DoctorNotificationIcon from '@/assets/icons/doctor-notification.vue'
import NotificationItemSkeleton from '@/components/designSystem/Skeleton/NotificationItemSkeleton.vue'
import type { WebNotification } from '@/types/web-notification.types'

const MESSAGE_TRUNCATE_THRESHOLD = 100

const router = useRouter()
const notificationStore = useNotificationStore()
const modalStore = useModalStore()
const { modals } = storeToRefs(modalStore)
const { notifyError } = useErrorToast()

const notificationOpen = ref(false)
const detailNotification = ref<WebNotification | null>(null)

const notifications = computed(() => notificationStore.notifications)
const unreadCount = computed(() => notificationStore.unreadCount)
const hasMore = computed(() => notificationStore.hasMore)
const isLoading = computed(() => notificationStore.isLoading)

function isMessageLong(message: string): boolean {
  return !!message && message.length > MESSAGE_TRUNCATE_THRESHOLD
}

function toggleNotifications() {
  notificationOpen.value = !notificationOpen.value
  if (notificationOpen.value) {
    notificationStore
      .fetchNotifications()
      .catch((e: unknown) => notifyError(e, 'Não foi possível carregar as notificações.'))
  }
}

/** Marca como lida sem bloquear a navegação; se falhar, a notificação segue não lida. */
function markAsRead(notification: WebNotification) {
  if (notification.isRead) return
  notificationStore
    .markAsRead(notification.id)
    .catch((e: unknown) => notifyError(e, 'Não foi possível marcar a notificação como lida.'))
}

function handleNotificationClick(notification: WebNotification) {
  if (notification.targetUrl) {
    // navigateTo já marca como lida — chamar aqui também duplicaria o PATCH (e o toast).
    notificationOpen.value = false
    navigateTo(notification)
    return
  }
  markAsRead(notification)
}

function isSafeUrl(url: string): boolean {
  if (url.startsWith('/') && !url.startsWith('//')) return true

  try {
    const parsed = new URL(url, window.location.origin)
    return parsed.origin === window.location.origin
  } catch {
    return false
  }
}

function navigateTo(notification: WebNotification) {
  markAsRead(notification)
  notificationOpen.value = false
  if (notification.targetUrl) {
    if (!isSafeUrl(notification.targetUrl)) return

    if (notification.targetUrl.startsWith('/')) {
      router.push(notification.targetUrl)
    } else {
      window.open(notification.targetUrl, '_blank', 'noopener,noreferrer')
    }
  }
}

function openDetailModal(notification: WebNotification) {
  markAsRead(notification)
  notificationOpen.value = false
  detailNotification.value = notification
  modalStore.toggleModal(EModals.NOTIFICATION_DETAIL)
}

function handleDetailAction() {
  if (detailNotification.value) {
    navigateTo(detailNotification.value)
  }
  closeDetailModal()
}

function closeDetailModal() {
  modalStore.toggleModal(EModals.NOTIFICATION_DETAIL)
  detailNotification.value = null
}

function handleMarkAllAsRead() {
  notificationStore
    .markAllAsRead()
    .catch((e: unknown) =>
      notifyError(e, 'Não foi possível marcar todas as notificações como lidas.')
    )
}

function handleLoadMore() {
  notificationStore
    .loadMore()
    .catch((e: unknown) => notifyError(e, 'Não foi possível carregar mais notificações.'))
}

function formatTime(dateString: string): string {
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return ''
  const now = new Date()
  const diffMs = Math.max(0, now.getTime() - date.getTime())
  const diffMinutes = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMinutes < 1) return 'Agora'
  if (diffMinutes < 60) return `${diffMinutes}min atrás`
  if (diffHours < 24) return `${diffHours}h atrás`
  if (diffDays < 7) return `${diffDays}d atrás`
  return date.toLocaleDateString('pt-BR')
}

onMounted(() => {
  notificationStore.fetchNotifications().catch(() => {
    // fallback intencional: pré-carga em background no mount do Header — abrir o sino
    // refaz a busca (toggleNotifications) e aí a falha vira toast.
  })
})
</script>
